import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { TierGate } from "@/components/TierGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, Link as LinkIcon, Copy, Trash2, Crown, User, Plus, Swords, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartyPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingMember, setLinkingMember] = useState(null);
  const [selectedCharId, setSelectedCharId] = useState("");
  const [newInviteCode, setNewInviteCode] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campaignRes, membersRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/members`, { withCredentials: true }),
      ]);
      setCampaign(campaignRes.data);
      setMembers(membersRes.data);

      if (campaignRes.data.role === "dm") {
        const [invitesRes, charsRes] = await Promise.all([
          axios.get(`${API}/campaigns/${campaignId}/invites`, { withCredentials: true }),
          axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }),
        ]);
        setInvites(invitesRes.data);
        setCharacters(charsRes.data.filter((c) => c.character_type === "pc"));
      }
    } catch (error) {
      toast.error("Failed to load party data");
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    try {
      const response = await axios.post(
        `${API}/campaigns/${campaignId}/invites`,
        { expires_in_days: null, max_uses: null },
        { withCredentials: true }
      );
      setNewInviteCode(response.data.invite_code);
      fetchData();
    } catch (error) {
      toast.error("Failed to create invite");
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied!");
  };

  const deleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/campaigns/${campaignId}/invites/${inviteId}`, { withCredentials: true });
      toast.success("Invite deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invite");
    }
  };

  const removeMember = async (userId) => {
    const ok = await confirm("Remove Player", "Remove this player from the campaign?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/campaigns/${campaignId}/members/${userId}`, { withCredentials: true });
      toast.success("Member removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const openLinkDialog = (member) => {
    setLinkingMember(member);
    setSelectedCharId(member.linked_character_id || "");
    setLinkDialogOpen(true);
  };

  const linkCharacter = async () => {
    if (!linkingMember) return;
    try {
      await axios.put(
        `${API}/campaigns/${campaignId}/members/${linkingMember.member_id}/link-character`,
        { character_id: (selectedCharId && selectedCharId !== "none") ? selectedCharId : null },
        { withCredentials: true }
      );
      toast.success((selectedCharId && selectedCharId !== "none") ? "Character linked!" : "Character unlinked");
      setLinkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to link character");
    }
  };

  const isDM = campaign?.role === "dm";

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  const getLinkedCharName = (charId) => {
    const c = characters.find((ch) => ch.id === charId);
    return c ? c.name : null;
  };

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="party-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">The Party</h1>
            <p className="text-muted-foreground text-sm">
              {members.length} {members.length === 1 ? "member" : "members"} in this campaign
            </p>
          </div>
        </div>

        <TierGate feature="Party & Sharing" description="Invite friends to join your campaigns, manage party members, and share your world with fellow adventurers.">
          {isDM && (
            <div className="flex gap-2 mb-6">
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => { setNewInviteCode(null); setInviteDialogOpen(true); }}
                    data-testid="invite-players-button"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Invite Players
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Invite Players</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {newInviteCode ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">Share this permanent link with your players:</p>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={`${window.location.origin}/join/${newInviteCode}`}
                            className="bg-input border-border font-mono text-sm"
                            data-testid="invite-link-input"
                          />
                          <Button onClick={() => copyInviteLink(newInviteCode)} variant="outline" className="shrink-0" data-testid="copy-invite-button">
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          This link never expires. Players just enter their name and character name to join — no account needed.
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <LinkIcon className="w-12 h-12 text-primary mx-auto mb-4" />
                        <p className="text-muted-foreground mb-2">
                          Generate a permanent invite link for your players
                        </p>
                        <p className="text-xs text-muted-foreground mb-4">
                          No login required — players just enter their name to join
                        </p>
                        <Button onClick={createInvite} data-testid="generate-invite-button" className="bg-primary text-primary-foreground">
                          Generate Invite Link
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Members List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {members.map((member) => (
              <Card key={member.member_id || member.user_id} className="bg-card border-border" data-testid={`member-card-${member.member_id || member.user_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={member.picture} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {member.role === "dm" ? <Crown className="w-5 h-5" /> : <User className="w-5 h-5" />}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-foreground truncate">
                          {member.role === "dm" ? member.name : (member.character_name || member.name)}
                        </h3>
                        {member.role === "dm" && (
                          <Badge variant="outline" className="shrink-0 border-primary text-primary text-xs">DM</Badge>
                        )}
                        {member.is_guest && member.role !== "dm" && (
                          <Badge variant="outline" className="shrink-0 border-muted-foreground/30 text-muted-foreground text-xs">Guest</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {member.role === "dm" ? "Dungeon Master" : (member.player_name || member.name)}
                      </p>
                      {member.linked_character_id && member.role !== "dm" && (
                        <div className="flex items-center gap-1 mt-1">
                          <Swords className="w-3 h-3 text-primary" />
                          <span className="text-xs text-primary">
                            {getLinkedCharName(member.linked_character_id) || "Character linked"}
                          </span>
                        </div>
                      )}
                    </div>
                    {isDM && member.role !== "dm" && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-primary h-8 w-8"
                          onClick={() => openLinkDialog(member)}
                          title="Link character"
                          data-testid={`link-char-${member.member_id}`}
                        >
                          <UserCheck className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive h-8 w-8"
                          onClick={() => removeMember(member.user_id)}
                          data-testid={`remove-member-${member.member_id || member.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Active Invites (DM Only) */}
          {isDM && invites.length > 0 && (
            <div>
              <h2 className="text-xl font-serif text-foreground mb-4">Active Invites</h2>
              <div className="space-y-2">
                {invites.map((invite) => (
                  <Card key={invite.id} className="bg-card border-border">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <LinkIcon className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <code className="text-sm text-foreground">{invite.invite_code}</code>
                          <p className="text-xs text-muted-foreground">
                            Used {invite.uses} times
                            {invite.max_uses && ` / ${invite.max_uses} max`}
                            {invite.expires_at ? ` — Expires ${new Date(invite.expires_at).toLocaleDateString()}` : " — Permanent"}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => copyInviteLink(invite.invite_code)} title="Copy link" data-testid={`copy-invite-${invite.invite_code}`}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteInvite(invite.id)} title="Delete invite" data-testid={`delete-invite-${invite.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Link Character Dialog */}
          <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">
                  Link Character to {linkingMember?.player_name || linkingMember?.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <p className="text-sm text-muted-foreground">
                  Choose a character sheet from your campaign to link to this player. They'll be able to view and edit it from their Player Portal.
                </p>
                <Select value={selectedCharId} onValueChange={setSelectedCharId}>
                  <SelectTrigger className="bg-input border-border" data-testid="select-character-trigger">
                    <SelectValue placeholder="Select a character..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No character (unlink)</SelectItem>
                    {characters.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} — {c.race} {c.character_class} (Lv.{c.level})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
                  <Button onClick={linkCharacter} className="bg-primary text-primary-foreground" data-testid="confirm-link-button">
                    {selectedCharId && selectedCharId !== "none" ? "Link Character" : "Unlink"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TierGate>
      </div>
    </Layout>
  );
};

export default PartyPage;
